"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PayoutSummaryResponse = exports.PayoutStatusSummary = exports.PayoutSummaryQuery = exports.RetryPayoutBatchBody = exports.RetryPayoutBatchItem = exports.DownloadRemittanceBody = exports.ApplyPayoutClearingBatchResponse = exports.ApplyPayoutClearingBatchResult = exports.ApplyPayoutClearingBatchBody = exports.ApplyPayoutClearingBatchAssignment = exports.ApplyPayoutClearingResponse = exports.ApplyPayoutClearingBody = exports.GetPayoutClearingsQuery = exports.GetPayoutClearingsResponse = exports.PayoutClearingEvent = exports.ResendRemittanceResponse = exports.ResendRemittanceBody = exports.CashFlowProjectionResponse = exports.CashFlowPayoutProjection = exports.CashFlowProjectionQuery = exports.RevertToPendingApprovalBody = exports.RejectPayoutBody = exports.AddPayablesToPayoutBody = exports.CreatePayoutBody = exports.PayoutListResponse = exports.PayoutsQuery = exports.PayoutsSortOrder = exports.PayoutsSortOrderValues = exports.PayoutsSortBy = exports.PayoutsSortByValues = exports.PayoutDetailTransport = exports.PayoutAttemptSummary = exports.PayoutTransport = exports.PayoutMethodProviderLabels = exports.PayoutMethodProvider = exports.PayoutMethodProviders = exports.PayoutBatchStatus = exports.PayoutPaymentMethod = void 0;
const typebox_1 = require("@sinclair/typebox");
const common_1 = require("../common");
const payable_transport_1 = require("./payable-transport");
const payout_approval_transport_1 = require("./payout-approval-transport");
const payout_attempt_transport_1 = require("./payout-attempt-transport");
const virtual_credit_card_transport_1 = require("./virtual-credit-card-transport");
/**
 * Payout Transport Types
 *
 * Represents payout batches for processing vendor payments.
 * A payout batch contains multiple payables and tracks the complete
 * lifecycle from draft through completion or failure.
 */
// Payment method - use common type for full set of payment methods
// Includes: ACCOUNT_CREDIT, ACH, CASH, CHECK, CREDIT_CARD, REVENUE_SHARE, WIRE
exports.PayoutPaymentMethod = common_1.PaymentMethod;
// Keep a local alias for backwards compatibility within this file
const PaymentMethod = exports.PayoutPaymentMethod;
// Payout batch status enum
exports.PayoutBatchStatus = typebox_1.Type.Union([
    typebox_1.Type.Literal('DRAFT'),
    typebox_1.Type.Literal('PENDING_APPROVAL'),
    typebox_1.Type.Literal('APPROVED'),
    typebox_1.Type.Literal('REJECTED'),
    typebox_1.Type.Literal('PROCESSING'),
    typebox_1.Type.Literal('AUTHORIZED'),
    typebox_1.Type.Literal('COMPLETED'),
    typebox_1.Type.Literal('FAILED'),
    typebox_1.Type.Literal('CANCELLED'),
]);
// Combined method+provider classification for payouts.
// Computed server-side: actual attempt provider when processed, predicted from
// vendor Plaid/SVB readiness when not yet processed. Display order matters —
// the admin-portal filter renders these in array order.
exports.PayoutMethodProviders = [
    'ACH_PLAID',
    'VCC_SVB',
    'ACH_MANUAL',
    'CREDIT_CARD_MANUAL',
    'CHECK',
    'OTHER',
];
exports.PayoutMethodProvider = typebox_1.Type.Union(exports.PayoutMethodProviders.map((m) => typebox_1.Type.Literal(m)));
exports.PayoutMethodProviderLabels = {
    ACH_PLAID: 'ACH (Plaid)',
    VCC_SVB: 'VCC (SVB)',
    ACH_MANUAL: 'ACH (Manual)',
    CREDIT_CARD_MANUAL: 'Credit Card (Manual)',
    CHECK: 'Check',
    OTHER: 'Other',
};
// Main transport schema for payouts
exports.PayoutTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    organizationID: typebox_1.Type.String({ format: 'uuid' }),
    totalAmount: typebox_1.Type.Integer({ minimum: 0 }),
    payableCount: typebox_1.Type.Integer({ minimum: 0 }),
    status: exports.PayoutBatchStatus,
    paymentMethod: PaymentMethod,
    requiresApproval: typebox_1.Type.Boolean(),
    approvalThreshold: typebox_1.Type.Integer({ minimum: 0 }),
    createdBy: typebox_1.Type.String({ format: 'uuid' }),
    submittedBy: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    submittedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    approvedBy: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    approvedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    rejectedBy: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    rejectedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    rejectionReason: typebox_1.Type.Optional(typebox_1.Type.String()),
    revertedBy: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    revertedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    scheduledPayoutDate: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    processingStartedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    completedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    attempts: typebox_1.Type.Array(payout_attempt_transport_1.PayoutAttemptTransport),
    // Remittance email tracking
    remittanceSentAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    remittanceRecipients: typebox_1.Type.Optional(typebox_1.Type.Array(typebox_1.Type.String())),
    // Computed status fields for UI
    // True if payout has a MANUAL provider attempt with PENDING status (needs user to record payment)
    awaitingManualPayment: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    // Combined method+provider bucket, computed at the API layer (see PayoutMethodProviders)
    methodProvider: typebox_1.Type.Optional(exports.PayoutMethodProvider),
    // Entity metadata
    createdAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    updatedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
});
// Payout attempt summary for payout details response
exports.PayoutAttemptSummary = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    attemptTimestamp: typebox_1.Type.String(), // ISO 8601 timestamp
    paymentMethod: PaymentMethod,
    provider: typebox_1.Type.Union([typebox_1.Type.Literal('PLAID'), typebox_1.Type.Literal('SVB'), typebox_1.Type.Literal('MANUAL')]),
    amount: typebox_1.Type.Integer({ minimum: 0 }),
    status: payout_attempt_transport_1.AttemptStatus,
    failureCode: typebox_1.Type.Optional(typebox_1.Type.String()),
    failureMessage: typebox_1.Type.Optional(typebox_1.Type.String()),
    completedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    failedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    providerTransferID: typebox_1.Type.Optional(typebox_1.Type.String()),
    providerAuthorizationID: typebox_1.Type.Optional(typebox_1.Type.String()),
    // ACH destination snapshot
    accountMask: typebox_1.Type.Optional(typebox_1.Type.String()),
    accountName: typebox_1.Type.Optional(typebox_1.Type.String()),
    accountType: typebox_1.Type.Optional(typebox_1.Type.String()),
    // VCC destination snapshot
    cardLastFour: typebox_1.Type.Optional(typebox_1.Type.String()),
});
// Extended payout transport with payables and attempts (for detail view)
exports.PayoutDetailTransport = typebox_1.Type.Intersect([
    exports.PayoutTransport,
    typebox_1.Type.Object({
        payables: typebox_1.Type.Array(payable_transport_1.PayableTransport),
        attempts: typebox_1.Type.Array(exports.PayoutAttemptSummary),
    }),
]);
// Sortable fields for payouts list
exports.PayoutsSortByValues = ['scheduledPayoutDate', 'createdAt', 'totalAmount', 'status', 'vendorID'];
exports.PayoutsSortBy = typebox_1.Type.Union(exports.PayoutsSortByValues.map((v) => typebox_1.Type.Literal(v)));
// Sort order
exports.PayoutsSortOrderValues = ['asc', 'desc'];
exports.PayoutsSortOrder = typebox_1.Type.Union(exports.PayoutsSortOrderValues.map((v) => typebox_1.Type.Literal(v)));
// Query parameters for listing payouts
// Default sort: scheduledPayoutDate asc (soonest payouts first)
exports.PayoutsQuery = typebox_1.Type.Object({
    vendorID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    status: typebox_1.Type.Optional(exports.PayoutBatchStatus),
    // Comma-separated PayoutBatchStatus values, for callers that want several at
    // once (e.g. every in-flight VCC payout, which spans PROCESSING and
    // AUTHORIZED). Takes precedence over `status` when both are given.
    statuses: typebox_1.Type.Optional(typebox_1.Type.String()),
    paymentMethod: typebox_1.Type.Optional(exports.PayoutPaymentMethod),
    // Comma-separated PayoutMethodProvider values (multi-select filter)
    methodProviders: typebox_1.Type.Optional(typebox_1.Type.String()),
    minAmount: typebox_1.Type.Optional(typebox_1.Type.String()), // String because query params come as strings
    maxAmount: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Date filters on scheduledPayoutDate (YYYY-MM-DD format)
    startDate: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date' })),
    endDate: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date' })),
    limit: typebox_1.Type.Optional(typebox_1.Type.String()), // String because query params come as strings
    offset: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Sorting - defaults to scheduledPayoutDate asc if not specified
    sortBy: typebox_1.Type.Optional(exports.PayoutsSortBy),
    sortOrder: typebox_1.Type.Optional(exports.PayoutsSortOrder),
});
// Response for listing payouts - uses standard PaginatedResult
exports.PayoutListResponse = (0, common_1.PaginatedResult)(exports.PayoutTransport);
// Request body for POST /invoice/payouts
exports.CreatePayoutBody = typebox_1.Type.Object({
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    paymentMethod: exports.PayoutPaymentMethod,
    scheduledPayoutDate: typebox_1.Type.Optional(typebox_1.Type.String()),
});
// Request body for POST /invoice/payouts/:id/payables
exports.AddPayablesToPayoutBody = typebox_1.Type.Object({
    payableIDs: typebox_1.Type.Array(typebox_1.Type.String({ format: 'uuid' })),
});
// Request body for POST /invoice/payouts/:id/reject
exports.RejectPayoutBody = typebox_1.Type.Object({
    reason: typebox_1.Type.String(),
});
// Request body for POST /invoice/payouts/:id/revert-to-pending-approval
// Empty body — reserved for future flags
exports.RevertToPendingApprovalBody = typebox_1.Type.Object({});
// Query params for GET /invoice/payouts/cash-flow
exports.CashFlowProjectionQuery = typebox_1.Type.Object({
    vendorID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    days: typebox_1.Type.Optional(typebox_1.Type.Number()),
});
// Individual payout in cash flow projection
exports.CashFlowPayoutProjection = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    scheduledPayoutDate: typebox_1.Type.Optional(typebox_1.Type.String()),
    amount: typebox_1.Type.Integer({ minimum: 0 }),
    payableCount: typebox_1.Type.Integer({ minimum: 0 }),
    status: exports.PayoutBatchStatus,
    paymentMethod: exports.PayoutPaymentMethod,
});
// Response for GET /invoice/payouts/cash-flow
exports.CashFlowProjectionResponse = typebox_1.Type.Object({
    vendorID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    projectionDays: typebox_1.Type.Integer({ minimum: 0 }),
    startDate: typebox_1.Type.String(),
    endDate: typebox_1.Type.String(),
    totalAmount: typebox_1.Type.Integer({ minimum: 0 }),
    payoutCount: typebox_1.Type.Integer({ minimum: 0 }),
    payouts: typebox_1.Type.Array(exports.CashFlowPayoutProjection),
});
// Request body for POST /invoice/payouts/:id/resend-remittance
exports.ResendRemittanceBody = typebox_1.Type.Object({
    emailOverrides: typebox_1.Type.Optional(typebox_1.Type.Array(typebox_1.Type.String({ format: 'email' }))),
});
// Response for POST /invoice/payouts/:id/resend-remittance
exports.ResendRemittanceResponse = typebox_1.Type.Object({
    success: typebox_1.Type.Boolean(),
    payoutID: typebox_1.Type.String({ format: 'uuid' }),
    sentTo: typebox_1.Type.Array(typebox_1.Type.String()),
    hasAttachment: typebox_1.Type.Boolean(),
});
// Response for GET /invoice/payouts/:id/clearings
exports.PayoutClearingEvent = typebox_1.Type.Object({
    approvalCode: typebox_1.Type.Optional(typebox_1.Type.String()),
    authorizationDate: typebox_1.Type.Optional(typebox_1.Type.String()),
    billing: typebox_1.Type.Object({ value: typebox_1.Type.Integer(), currencyCode: typebox_1.Type.String() }),
    clearingType: typebox_1.Type.Union([typebox_1.Type.Literal('DEBIT'), typebox_1.Type.Literal('CREDIT'), typebox_1.Type.Literal('NOT_PROVIDED')]),
    merchant: typebox_1.Type.Object({ value: typebox_1.Type.Integer(), currencyCode: typebox_1.Type.String() }),
    merchantName: typebox_1.Type.Optional(typebox_1.Type.String()),
    merchantId: typebox_1.Type.Optional(typebox_1.Type.String()),
    mcc: typebox_1.Type.Optional(typebox_1.Type.String()),
    mccDescription: typebox_1.Type.Optional(typebox_1.Type.String()),
    vcnUuid: typebox_1.Type.String(),
    settlementDate: typebox_1.Type.Optional(typebox_1.Type.String()),
    banknetReferenceNumber: typebox_1.Type.Optional(typebox_1.Type.String()),
    mastercardFinancialTransactionId: typebox_1.Type.Optional(typebox_1.Type.String()),
    appliedAmount: typebox_1.Type.Integer({ default: 0 }),
});
exports.GetPayoutClearingsResponse = typebox_1.Type.Object({
    // Resolved from the payout's in-progress SVB attempt, so it's always present.
    // Not redundant with `card.svbCardId`: `card` is an optional lookup of our
    // local VirtualCreditCard record, which can be missing (e.g. the card was
    // cancelled or was never persisted locally) while the attempt still has an ID.
    svbCardId: typebox_1.Type.String({ format: 'uuid' }),
    card: typebox_1.Type.Optional(virtual_credit_card_transport_1.VirtualCreditCardTransport),
    // The payout date (scheduledPayoutDate, falling back to processingStartedAt)
    // the clearings filter is based on — shown in the UI for comparison
    payoutDate: typebox_1.Type.Optional(typebox_1.Type.String()),
    clearings: typebox_1.Type.Array(exports.PayoutClearingEvent),
});
// Query params for GET /invoice/payouts/:id/clearings — vendorID must match the
// payout's own vendorID (checked server-side) so a caller can't act on a payout
// belonging to a different vendor than the one they intend. startDate/endDate
// are an optional settlement date range filter (YYYY-MM-DD, inclusive both
// ends), defaulting to the payout date onward when omitted.
exports.GetPayoutClearingsQuery = typebox_1.Type.Object({
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    startDate: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date' })),
    endDate: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date' })),
});
// Request body for POST /invoice/payouts/:id/apply-clearing. vendorID must
// match the payout's own vendorID (checked server-side).
exports.ApplyPayoutClearingBody = typebox_1.Type.Object({
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    clearing: exports.PayoutClearingEvent,
});
// Response for POST /invoice/payouts/:id/apply-clearing
// Processing is asynchronous — the clearing is queued through the same pipeline
// as SVB's clearing webhook.
exports.ApplyPayoutClearingResponse = typebox_1.Type.Object({
    success: typebox_1.Type.Boolean(),
    payoutID: typebox_1.Type.String({ format: 'uuid' }),
});
// One payout-to-clearing pairing within a batch request. Each pairing gets its
// own clearing — reconciling several payouts in one pass often means several
// different settlement events, not one clearing split across many payouts.
exports.ApplyPayoutClearingBatchAssignment = typebox_1.Type.Object({
    payoutID: typebox_1.Type.String({ format: 'uuid' }),
    clearing: exports.PayoutClearingEvent,
});
// Request body for POST /invoice/payouts/apply-clearing-batch. vendorID scopes
// the WHOLE batch — every payoutID in assignments must belong to this vendor.
// Clearings are never reconciled across vendors, so one vendorID per batch,
// not per assignment.
exports.ApplyPayoutClearingBatchBody = typebox_1.Type.Object({
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    assignments: typebox_1.Type.Array(exports.ApplyPayoutClearingBatchAssignment, { minItems: 1, maxItems: 100 }),
});
// Response for POST /invoice/payouts/apply-clearing-batch
// Each payout is applied independently server-side so one failure (e.g. a stale
// selection, or a payout that moved out of PROCESSING) doesn't block the rest.
exports.ApplyPayoutClearingBatchResult = typebox_1.Type.Object({
    payoutID: typebox_1.Type.String({ format: 'uuid' }),
    success: typebox_1.Type.Boolean(),
    error: typebox_1.Type.Optional(typebox_1.Type.String()),
});
exports.ApplyPayoutClearingBatchResponse = typebox_1.Type.Object({
    results: typebox_1.Type.Array(exports.ApplyPayoutClearingBatchResult),
});
// Request body for POST /invoice/payouts/download-remittance
exports.DownloadRemittanceBody = typebox_1.Type.Object({
    payoutIds: typebox_1.Type.Array(typebox_1.Type.String({ format: 'uuid' }), { minItems: 1, maxItems: 100 }),
});
// Request body for POST /invoice/payouts/retry-batch — the same per-payout
// shape as the single-payout retry endpoint (id + overrides), wrapped in an array.
exports.RetryPayoutBatchItem = typebox_1.Type.Intersect([
    typebox_1.Type.Object({ id: typebox_1.Type.String({ format: 'uuid' }) }),
    payout_approval_transport_1.ApprovePayoutOverrides,
]);
exports.RetryPayoutBatchBody = typebox_1.Type.Array(exports.RetryPayoutBatchItem, { minItems: 1 });
// Query params for GET /invoice/payouts/summary
exports.PayoutSummaryQuery = typebox_1.Type.Object({
    vendorID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    paymentMethod: typebox_1.Type.Optional(exports.PayoutPaymentMethod),
});
// Status summary (count and total amount)
exports.PayoutStatusSummary = typebox_1.Type.Object({
    count: typebox_1.Type.Integer({ minimum: 0 }),
    totalAmount: typebox_1.Type.Integer({ minimum: 0 }),
});
// Response for GET /invoice/payouts/summary
exports.PayoutSummaryResponse = typebox_1.Type.Object({
    // Breakdown by status
    byStatus: typebox_1.Type.Object({
        DRAFT: exports.PayoutStatusSummary,
        PENDING_APPROVAL: exports.PayoutStatusSummary,
        APPROVED: exports.PayoutStatusSummary,
        REJECTED: exports.PayoutStatusSummary,
        PROCESSING: exports.PayoutStatusSummary,
        AUTHORIZED: exports.PayoutStatusSummary,
        COMPLETED: exports.PayoutStatusSummary,
        FAILED: exports.PayoutStatusSummary,
        CANCELLED: exports.PayoutStatusSummary,
    }),
    // Breakdown by payment method
    byPaymentMethod: typebox_1.Type.Record(typebox_1.Type.String(), exports.PayoutStatusSummary),
    // Overall totals
    totals: typebox_1.Type.Object({
        /** Sum of DRAFT + PENDING_APPROVAL + APPROVED + PROCESSING + AUTHORIZED */
        pending: exports.PayoutStatusSummary,
        /** Sum of COMPLETED */
        completed: exports.PayoutStatusSummary,
        /** Sum of FAILED */
        failed: exports.PayoutStatusSummary,
        /** Sum of CANCELLED + REJECTED */
        cancelled: exports.PayoutStatusSummary,
    }),
    // Operational metrics
    operational: typebox_1.Type.Object({
        /** Payouts completed today */
        completedToday: exports.PayoutStatusSummary,
        /** Payouts completed this month (Eastern timezone) */
        completedThisMonth: exports.PayoutStatusSummary,
        /** Payouts scheduled for today */
        scheduledToday: exports.PayoutStatusSummary,
        /** Payouts past their scheduled date but not completed */
        overdue: exports.PayoutStatusSummary,
    }),
    // Historical metrics
    historical: typebox_1.Type.Object({
        /** Date of most recent completed payout (ISO 8601) */
        lastPayoutDate: typebox_1.Type.Union([typebox_1.Type.String(), typebox_1.Type.Null()]),
        /** Average payout amount (completed payouts only) */
        averagePayoutAmount: typebox_1.Type.Integer({ minimum: 0 }),
        /** Total unique vendors with payouts */
        uniqueVendorCount: typebox_1.Type.Integer({ minimum: 0 }),
    }),
    // Filter context (echoes back applied filters)
    filters: typebox_1.Type.Object({
        vendorID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
        paymentMethod: typebox_1.Type.Optional(typebox_1.Type.String()),
    }),
});
//# sourceMappingURL=payout-transport.js.map